import type { MockEnvironment } from '@gotti/test-utils';
import { InputSimulator, clearFocusedClients, getActiveEnv, resetOrderedMessageQueues, teardownEnv } from '@gotti/test-utils';
import type { PlaygroundPairModule } from '../config/pair.js';
import type {
  PlaygroundDefinitionModule,
  PlaygroundMode,
  PlaygroundModule,
  PlaygroundRuntimeSelection,
  PlaygroundTestListing,
  PlaygroundTestProgress,
} from '../types.js';
import { bootPlaygroundSelection, resolvePlaygroundSelection } from './boot.js';
import { listClients } from './clients.js';
import { clearCurrentPlaygroundContext } from './context.js';
import { runPlaygroundTestSuite, type PlaygroundTestSuiteResult } from './tests.js';
import { toTestListing } from '../config/tests.js';

/** What `mountPlayground` takes: an evaluated definition (or pair), the config it layers over, and where to draw. */
export interface MountPlaygroundOptions {
  definition: PlaygroundDefinitionModule | PlaygroundPairModule<any, any, any> | { default?: PlaygroundDefinitionModule | PlaygroundPairModule<any, any, any> };
  /** The config the definition is layered over (`clientConfig`, `mockConfig`, `gameHooks`). Default `{}`. */
  config?: PlaygroundModule | { default?: PlaygroundModule };
  /** For a pair: which half to boot. Default `'online'`. */
  variant?: PlaygroundMode;
  /** The playground's name — what results and errors call it. */
  name: string;
  /** The element the playground renders into. A host owns its page, so there is no default. */
  target: HTMLElement;
  /**
   * Aborting it while the playground boots gives the boot up: `mountPlayground` rejects with a
   * `PlaygroundMountCancelledError`, what the boot started so far is stopped, and the page is free for the next mount.
   * The boot's own code cannot be stopped — a `setup` still awaiting goes on — but the boot runs nothing more once it
   * resumes (no default setup, no game hooks, no runtime context), and what it built is stopped then, without touching
   * a mount live by that time. No effect once the mount has resolved: `dispose()` ends a mount.
   */
  signal?: AbortSignal;
}

/** A boot its host gave up on through `MountPlaygroundOptions.signal`. */
export class PlaygroundMountCancelledError extends Error {
  constructor(name: string) {
    super(`[gotti][playground] Playground "${name}" stopped booting: its host cancelled the mount.`);
    this.name = 'PlaygroundMountCancelledError';
  }
}

/** How one `runTests` call runs. */
export interface PlaygroundMountRunOptions {
  /** Run only the test with this label or id. */
  targetLabel?: string;
  /** Called with a fresh snapshot of a test's progress tree each time it changes — never the tree the runner mutates. */
  onProgress?: (progress: PlaygroundTestProgress) => void;
  /** Aborting it cancels the run: the step running then is abandoned, nothing after it runs, and the result says `cancelled`. */
  signal?: AbortSignal;
}

/** A playground booted into a page element by `mountPlayground`. */
export interface PlaygroundMount {
  readonly name: string;
  readonly selection: PlaygroundRuntimeSelection;
  readonly environment: MockEnvironment | null;
  readonly target: HTMLElement;
  /** The definition's tests, in run order. */
  listTests(): PlaygroundTestListing[];
  /** Runs the tests against this mount's environment. One run at a time; throws after `dispose()`. */
  runTests(options?: PlaygroundMountRunOptions): Promise<PlaygroundTestSuiteResult>;
  /**
   * Cancels a run in flight and waits for it to settle, stops every client and area loop, tears down test-utils'
   * page-wide state (the active environment, the ordered message queues, focus routing, the input simulator), forgets
   * the runtime context and deletes the page handle, and removes what the mount added to `target`. Idempotent. All of
   * it happens even when a system's `onStop` throws: what was thrown is raised afterwards, and the page is free for the
   * next mount either way.
   */
  dispose(): Promise<void>;
}

/** The mount that is live in this page: test-utils' environment and input state are page-wide, so there is one at a time. */
let live: { name: string; environment: MockEnvironment | null } | null = null;

/** The environment test-utils holds active, or null: `getActiveEnv()` throws when there is none, an answer here. */
const activeEnv = (): MockEnvironment | null => {
  try {
    return getActiveEnv();
  } catch {
    return null;
  }
};

/** Stops every area and client loop of `environments`, and test-utils' hold on whichever is active; what `onStop` throws is collected and returned. */
const stopEnvironments = (environments: readonly MockEnvironment[]): unknown[] => {
  const errors: unknown[] = [];
  for (const environment of environments) {
    for (const area of Object.values(environment.areas ?? {})) attempt(errors, () => area.gameProcess?.stopLoop?.());
    for (const client of listClients(environment)) attempt(errors, () => client.process?.stopLoop?.());
    if (activeEnv() === environment) attempt(errors, teardownEnv);
  }
  return errors;
};

/** Runs `stop`, collecting what it throws so the rest of the teardown still runs. */
const attempt = (errors: unknown[], stop: () => void): void => {
  try {
    stop();
  } catch (err) {
    errors.push(err);
  }
};

/**
 * Ends `environments` — every loop, then test-utils' hold on the active one — and the page-wide state, and removes
 * what the mount added to `target`. Stopping a loop runs the systems' `onStop`, which is user code: what any of it
 * throws is collected and returned, and the teardown carries on regardless.
 */
const teardown = (environments: readonly MockEnvironment[], target: HTMLElement, hostNodes: ReadonlySet<ChildNode>): unknown[] => {
  const errors = stopEnvironments(environments);
  resetOrderedMessageQueues();
  clearFocusedClients();
  InputSimulator.reset();
  clearCurrentPlaygroundContext();
  for (const node of Array.from(target.childNodes)) {
    if (!hostNodes.has(node)) target.removeChild(node);
  }
  return errors;
};

/**
 * Boots a playground into `target` without the CLI: the same resolve-and-boot `bootstrapPlayground` runs, but none of
 * the runner's page globals, no READY flag and no URL-triggered run — only the page handle
 * (`PLAYGROUND_GLOBALS.environment`), published before the mount resolves. Tests run when the host calls `runTests`;
 * `dispose` ends it, after which another playground can be mounted. Throws while another mount is live, naming it.
 */
export const mountPlayground = async (options: MountPlaygroundOptions): Promise<PlaygroundMount> => {
  const { target } = options;
  // Checked before the page is claimed, so a host that looked its element up too early can simply retry.
  if (!target) {
    throw new Error(`[gotti][playground] Playground "${options.name}" needs a target element to render into; got ${String(target)}.`);
  }
  if (options.signal?.aborted) throw new PlaygroundMountCancelledError(options.name);
  if (live) {
    throw new Error(`[gotti][playground] Playground "${live.name}" is still mounted in this page: dispose() it before mounting "${options.name}".`);
  }
  const hostNodes = new Set(Array.from(target.childNodes));
  const token: { name: string; environment: MockEnvironment | null } = { name: options.name, environment: null };
  live = token;
  // An environment made active after this point and never handed back — the boot failed after `initializer` ran, or
  // a `setup` kept the one it built to itself — is still this mount's to end.
  const activeBefore = activeEnv();

  let selection: PlaygroundRuntimeSelection;
  let environment: MockEnvironment | null = null;
  const owned = (): MockEnvironment[] => {
    const active = activeEnv();
    const unclaimed = active && active !== activeBefore && active !== environment ? [active] : [];
    return environment ? [environment, ...unclaimed] : unclaimed;
  };
  // The boot itself, which a host's signal can give up on (it cannot stop it: a `setup` still awaiting goes on).
  const giveUp = new AbortController();
  const booting = (async () => {
    const resolved = await resolvePlaygroundSelection({
      module: options.config ?? {},
      definition: options.definition,
      variant: options.variant,
      playgroundName: options.name,
    });
    if (giveUp.signal.aborted) return { resolved, built: null };
    return { resolved, built: await bootPlaygroundSelection(resolved, target, { signal: giveUp.signal }) };
  })();
  try {
    const booted = await new Promise<Awaited<typeof booting>>((resolve, reject) => {
      const onAbort = () => reject(new PlaygroundMountCancelledError(options.name));
      options.signal?.addEventListener('abort', onAbort, { once: true });
      booting.then(resolve, reject).finally(() => options.signal?.removeEventListener('abort', onAbort));
    });
    selection = booted.resolved;
    environment = booted.built;
    token.environment = environment;
  } catch (err) {
    if (err instanceof PlaygroundMountCancelledError) {
      giveUp.abort();
      // What the boot builds after it was given up is stopped when it lands — its own environment only, never the
      // mount live by then, and the page-wide state only while the page is free.
      void booting.then(({ built }) => {
        if (!built || built === live?.environment) return;
        const errors = live ? stopEnvironments([built]) : teardown([built], target, hostNodes);
        for (const error of errors) {
          console.error(`[gotti][playground] Playground "${options.name}" was given up while it booted, and stopping what it built threw:`, error);
        }
      }, () => undefined);
    }
    // A boot that fails leaves nothing mounted: whatever it started is stopped and the target is as it was. The boot's
    // error is the one raised; what the teardown hits on the way is reported beside it.
    try {
      for (const error of teardown(owned(), target, hostNodes)) {
        console.error(`[gotti][playground] Playground "${options.name}" failed to boot, and tearing it down threw too:`, error);
      }
    } finally {
      live = null;
    }
    throw err;
  }

  let running: { controller: AbortController; done: Promise<PlaygroundTestSuiteResult> } | null = null;
  let disposed = false;

  return {
    name: options.name,
    selection,
    environment,
    target,
    listTests: () => selection.tests.map(toTestListing),
    runTests: async (run: PlaygroundMountRunOptions = {}) => {
      if (disposed) {
        throw new Error(`[gotti][playground] Playground "${options.name}" was disposed: mount it again to run its tests.`);
      }
      if (running) {
        throw new Error(`[gotti][playground] Playground "${options.name}" is already running its tests: await or cancel that run first.`);
      }
      // A `defineSandboxTest` test runs on its host's virtual clock, which only the host installs: without it, the
      // test's ticks would never fire.
      const toRun = run.targetLabel === undefined
        ? selection.tests
        : selection.tests.filter((test) => test.label === run.targetLabel || test.id === run.targetLabel);
      const clockless = toRun.find((test) => test.virtual && !test.virtual.clockInstalled());
      if (clockless) {
        throw new Error(`[gotti][playground] Playground "${options.name}" runs a defineSandboxTest test ("${clockless.label}"), which needs its host’s virtual clock installed.`);
      }
      // The mount's own controller, so `dispose` can cancel a run whatever signal the caller passed (or none).
      const controller = new AbortController();
      const forward = () => controller.abort();
      if (run.signal?.aborted) controller.abort();
      else run.signal?.addEventListener('abort', forward, { once: true });
      const done = runPlaygroundTestSuite({
        selection,
        environment,
        target,
        targetLabel: run.targetLabel,
        onProgress: run.onProgress,
        signal: controller.signal,
      });
      running = { controller, done };
      try {
        return await done;
      } finally {
        running = null;
        run.signal?.removeEventListener('abort', forward);
      }
    },
    dispose: async () => {
      if (disposed) return;
      disposed = true;
      try {
        if (running) {
          running.controller.abort();
          await running.done.catch(() => undefined);
        }
        // Everything is torn down before anything is raised, so a throwing `onStop` still leaves the page free.
        const errors = teardown(owned(), target, hostNodes);
        if (errors.length === 1) throw errors[0];
        if (errors.length > 1) {
          throw new AggregateError(errors, `[gotti][playground] Playground "${options.name}" was disposed, but stopping it threw ${errors.length} errors.`);
        }
      } finally {
        if (live === token) live = null;
      }
    },
  };
};
